// Ported to threeJS from BabylonJS playground https://www.babylonjs-playground.com/#YSQLI3

import {
	Group, Mesh, SphereGeometry, PlaneGeometry,
	InstancedBufferGeometry, InstancedBufferAttribute,
	ShaderMaterial, MeshBasicMaterial, Color,
	AdditiveBlending, DoubleSide, Sphere, Vector3,
	PointLight, AmbientLight,
} from 'three';

// Cull a whole sun when its bounding sphere leaves the view. Sized as a generous multiple of
// the sun radius to safely cover shader-side particle expansion (billboard size, tall corona
// scaleY, flare emit/grow) — over-sized so on/near-screen suns never pop; only far-off-screen
// suns (the bulk in the map overview) get culled. Bump this if any sun pops at a screen edge.
const SUN_CULL_RADIUS = 6;
import { textureLoader } from './instances.js';

// ── Textures ──
const brushTexture = textureLoader.load('/assets/gfx/textures/T_SunSurface.png');
const starTexture = textureLoader.load('/assets/gfx/textures/T_Star.png');
const flareTexture = textureLoader.load('/assets/gfx/textures/T_SunFlare.png');

// ── Palettes ──
const SUN_PALETTES = {
	YELLOW: { core: '#521200', c1: [0.85, 0.48, 0.10], c2: [0.63, 0.31, 0.06], c3: [0.32, 0.07, 0.01] },
	ORANGE: { core: '#4a0800', c1: [0.90, 0.45, 0.08], c2: [0.75, 0.25, 0.04], c3: [0.35, 0.06, 0.01] },
	RED: { core: '#400600', c1: [0.90, 0.25, 0.06], c2: [0.70, 0.10, 0.02], c3: [0.30, 0.03, 0.00] },
	BLUE: { core: '#050d4d', c1: [0.60, 0.78, 0.95], c2: [0.20, 0.38, 0.80], c3: [0.05, 0.10, 0.40] },
	WHITE: { core: '#804d1a', c1: [1.00, 0.95, 0.80], c2: [0.85, 0.70, 0.45], c3: [0.50, 0.30, 0.10] },
	MAGENTA: { core: '#400526', c1: [0.90, 0.40, 0.80], c2: [0.60, 0.15, 0.50], c3: [0.25, 0.03, 0.15] },
	BROWN: { core: '#260d02', c1: [0.80, 0.50, 0.20], c2: [0.45, 0.18, 0.05], c3: [0.15, 0.05, 0.01] },
	BLACK: { core: '#050000', c1: [0.25, 0.08, 0.04], c2: [0.10, 0.02, 0.01], c3: [0.03, 0.00, 0.00] },
};

// ── Shader source (shared, BILLBOARD define toggles mode) ──
const vertSrc = /* glsl */ `
attribute vec3 aPos;
attribute float aSpawn;
attribute float aRot;
attribute float aASpeed;
attribute float aSize;
attribute float aScaleY;

uniform float uTime;
uniform float uLifetime;
uniform float uRadius;
uniform float uSizeOverLife; // 0 = constant, 1 = grows from 0 to full
uniform float uEmitSpeed;    // outward drift (0 = stays on surface)

varying float vAge;
varying vec2 vUv;

void main() {
	float age = mod(uTime - aSpawn, uLifetime);
	float t = age / uLifetime;
	vAge = t;
	vUv = uv;

	vec3 N = normalize(aPos);

	// Size: optionally grows over lifetime (flares start tiny, grow)
	float sizeMul = mix(1.0, t, uSizeOverLife);

	// Outward drift: particle moves away from surface over time
	float drift = uEmitSpeed * age;

#ifdef BILLBOARD
	// Billboard: always face camera, rotated by aRot so stretch direction varies.
	// normalize() so the quad size is independent of the model's world scale —
	// otherwise game.main's XR scale (0.01–0.25) bakes into the basis and the
	// quad shrinks to sub-pixel size, making corona/flares vanish in XR.
	vec3 right = normalize(vec3(modelViewMatrix[0][0], modelViewMatrix[1][0], modelViewMatrix[2][0]));
	vec3 camUp  = normalize(vec3(modelViewMatrix[0][1], modelViewMatrix[1][1], modelViewMatrix[2][1]));
	float sr = sin(aRot), cr = cos(aRot);
	vec3 r2 = right * cr + camUp * sr;
	vec3 u2 = -right * sr + camUp * cr;
	float sx = aSize * uRadius * sizeMul;
	float sy = aSize * uRadius * aScaleY * sizeMul;
	vec3 worldPos = N * (uRadius + drift) + r2 * position.x * sx + u2 * position.y * sy;
#else
	// Surface-oriented: tangent frame on sphere
	vec3 up = abs(N.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
	vec3 T = normalize(cross(up, N));
	vec3 B = cross(N, T);
	float angle = aRot + aASpeed * age;
	float sa = sin(angle), ca = cos(angle);
	vec3 T2 = T * ca + B * sa;
	vec3 B2 = -T * sa + B * ca;
	float s = aSize * uRadius * sizeMul;
	vec3 worldPos = N * (uRadius + drift) + T2 * position.x * s + B2 * position.y * s;
#endif

	gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
}
`;

const fragSrc = /* glsl */ `
uniform sampler2D uBrush;
uniform vec3 uC1, uC2, uC3;
uniform float uPeakAlpha;
uniform float uFadeIn;  // 0-1: fraction of lifetime to reach peak (default 0.4)

varying float vAge;
varying vec2 vUv;

void main() {
	float texA = texture2D(uBrush, vUv).a;
	float t = vAge;
	float mid = uFadeIn + 0.1; // small plateau after fade-in
	vec3 col;
	float a;
	if (t < uFadeIn) {
		float s = t / uFadeIn;
		col = mix(uC1, uC2, s);
		a = s * uPeakAlpha;
	} else if (t < mid) {
		col = uC2;
		a = uPeakAlpha;
	} else {
		float s = (t - mid) / (1.0 - mid);
		col = mix(uC2, uC3, s);
		a = uPeakAlpha * (1.0 - s);
	}
	gl_FragColor = vec4(col * a, texA);
}
`;

// ── Reusable GPU particle layer ──
const basePlane = new PlaneGeometry(1, 1);
const allMaterials = [];

export const planetLighting = {
	nightFillIntensity: 0.9,
};

export function setPlanetNightFillIntensity(value) {
	const intensity = Math.max(0, Number(value) || 0);
	const roots = [globalThis.game?.scene, globalThis.game?.main];
	const seen = new Set();

	planetLighting.nightFillIntensity = intensity;
	for (const root of roots) {
		if (!root || seen.has(root)) continue;
		seen.add(root);
		root.traverse?.(node => {
			if (node.userData?.planetNightFill) node.intensity = intensity;
		});
	}
	return intensity;
}

function randomOnSphere() {
	const theta = Math.random() * Math.PI * 2;
	const phi = Math.acos(2 * Math.random() - 1);
	const sinPhi = Math.sin(phi);
	return [sinPhi * Math.cos(theta), Math.cos(phi), sinPhi * Math.sin(theta)];
}

class SunParticleLayer {
	/**
	 * @param {object} opts
	 * @param {number} opts.count        — number of particles
	 * @param {number} opts.lifetime     — seconds per cycle
	 * @param {Texture} opts.texture     — particle texture (alpha channel)
	 * @param {boolean} opts.billboard   — true = face camera, false = face outward
	 * @param {number} opts.sizeMin      — minimum size (fraction of radius)
	 * @param {number} opts.sizeRange    — size variation
	 * @param {number} opts.scaleYMin    — minimum Y scale (billboard stretch)
	 * @param {number} opts.scaleYRange  — Y scale variation
	 * @param {number} opts.angularSpeed — max rotation speed (surface mode)
	 * @param {number} opts.peakAlpha    — maximum opacity in gradient
	 * @param {string} opts.sunType      — palette key
	 * @param {number} opts.radius       — sphere radius
	 */
	constructor(opts) {
		const geo = this._createGeometry(opts);
		const mat = this._getMaterial(opts);
		this.mesh = new Mesh(geo, mat);
		// A generous local bounding sphere so three can frustum-cull off-screen suns (the base
		// plane's own bounds would be tiny — particles are placed/expanded in the vertex shader —
		// so without this we had to disable culling entirely and every off-screen sun still drew).
		geo.boundingSphere = new Sphere(new Vector3(0, 0, 0), (opts.radius || 1) * SUN_CULL_RADIUS);
		this.mesh.frustumCulled = true;
	}

	_createGeometry(opts) {
		const {
			count, lifetime, sizeMin, sizeRange,
			scaleYMin = 1, scaleYRange = 0, angularSpeed = 0,
		} = opts;
		const geo = new InstancedBufferGeometry();
		geo.index = basePlane.index;
		geo.attributes.position = basePlane.attributes.position;
		geo.attributes.uv = basePlane.attributes.uv;

		const n = count;
		const pos = new Float32Array(n * 3);
		const spawn = new Float32Array(n);
		const rot = new Float32Array(n);
		const aspeed = new Float32Array(n);
		const sizes = new Float32Array(n);
		const scaleY = new Float32Array(n);

		for (let i = 0; i < n; i++) {
			const p = randomOnSphere();
			pos[i * 3] = p[0]; pos[i * 3 + 1] = p[1]; pos[i * 3 + 2] = p[2];
			spawn[i] = -Math.random() * lifetime;
			rot[i] = (Math.random() * 2 - 1) * Math.PI * 2;
			aspeed[i] = (Math.random() * 2 - 1) * angularSpeed;
			sizes[i] = sizeMin + Math.random() * sizeRange;
			scaleY[i] = scaleYMin + Math.random() * scaleYRange;
		}

		geo.setAttribute('aPos', new InstancedBufferAttribute(pos, 3));
		geo.setAttribute('aSpawn', new InstancedBufferAttribute(spawn, 1));
		geo.setAttribute('aRot', new InstancedBufferAttribute(rot, 1));
		geo.setAttribute('aASpeed', new InstancedBufferAttribute(aspeed, 1));
		geo.setAttribute('aSize', new InstancedBufferAttribute(sizes, 1));
		geo.setAttribute('aScaleY', new InstancedBufferAttribute(scaleY, 1));
		return geo;
	}

	_getMaterial(opts) {
		const {
			sunType, billboard, texture, peakAlpha, lifetime, radius,
			sizeOverLife = 0, emitSpeed = 0, fadeIn = 0.4,
		} = opts;
		const pal = SUN_PALETTES[sunType] || SUN_PALETTES.YELLOW;
		const defines = billboard ? { BILLBOARD: '' } : {};

		const mat = new ShaderMaterial({
			defines,
			uniforms: {
				uTime: { value: 0 },
				uLifetime: { value: lifetime },
				uRadius: { value: radius },
				uBrush: { value: texture },
				uPeakAlpha: { value: peakAlpha },
				uSizeOverLife: { value: sizeOverLife },
				uEmitSpeed: { value: emitSpeed },
				uFadeIn: { value: fadeIn },
				uC1: { value: new Color(...pal.c1) },
				uC2: { value: new Color(...pal.c2) },
				uC3: { value: new Color(...pal.c3) },
			},
			vertexShader: vertSrc,
			fragmentShader: fragSrc,
			transparent: true,
			blending: AdditiveBlending,
			depthWrite: false,
			side: DoubleSide,
		});
		allMaterials.push(mat);
		// Remove from the per-frame uTime list when the material is disposed (sun unloaded),
		// so the array doesn't grow unbounded and we don't tick disposed materials each frame.
		const _dispose = mat.dispose.bind(mat);
		mat.dispose = function () {
			const i = allMaterials.indexOf(mat);
			if (i !== -1) allMaterials.splice(i, 1);
			_dispose();
		};
		return mat;
	}
}

// ── Public API ──

export function createSunMesh(type, radius = 25) {
	const pal = SUN_PALETTES[type] || SUN_PALETTES.YELLOW;
	const group = new Group();

	// THE system's light source — planets (MeshStandardMaterial, see
	// planet-3rdgen.js) get their day/night sides from here. No distance
	// falloff (decay 0) so the outermost orbits stay lit; a soft ambient
	// keeps the night side readable.
	const sunlight = new PointLight(0xfff2e0, 3.2, 0, 0);
	const nightFill = new AmbientLight(0xffffff, planetLighting.nightFillIntensity);
	nightFill.name = 'PLANET_NIGHT_FILL';
	nightFill.userData.planetNightFill = true;

	group.add(sunlight);
	group.add(nightFill);

	// Dark emissive core
	group.add(new Mesh(
		new SphereGeometry(radius, 36, 36),
		new MeshBasicMaterial({ color: pal.core }),
	));

	// Surface particles — stick to sphere, slowly rotate
	const surface = new SunParticleLayer({
		count: 1000,
		lifetime: 10,
		texture: brushTexture,
		billboard: false,
		sizeMin: 0.5,
		sizeRange: 0.25,
		angularSpeed: 0.1,
		peakAlpha: 0.5,
		sunType: type,
		radius,
	});
	group.add(surface.mesh);

	// Corona particles — billboard, tall/thin, very subtle
	const corona = new SunParticleLayer({
		count: 600,
		lifetime: 4,
		texture: starTexture,
		billboard: true,
		sizeMin: 0.5,
		sizeRange: 0.7,
		scaleYMin: 1.5,
		scaleYRange: 2.0,
		angularSpeed: 0,
		peakAlpha: 0.12,
		sunType: type,
		radius,
	});
	group.add(corona.mesh);

	// Flare particles — rare, billboard, grow over time, drift outward
	const flares = new SunParticleLayer({
		count: 20,
		lifetime: 10,
		texture: flareTexture,
		billboard: true,
		sizeMin: 0.5,
		sizeRange: 0.5,
		scaleYMin: 0.5,
		scaleYRange: 0.5,
		angularSpeed: 0,
		peakAlpha: 1.0,
		fadeIn: 0.25,
		sizeOverLife: 1,
		emitSpeed: 0.3,
		sunType: type,
		radius,
	});
	group.add(flares.mesh);

	return group;
}

/** Call once per frame — updates uTime on all materials. */
export function updateSunParticles() {
	const t = performance.now() / 1000;
	for (const mat of allMaterials) {
		mat.uniforms.uTime.value = t;
	}
}
